import React from 'react'
import {
    Button,
    VStack,
    HStack,
    Box,
    Text,
    Select,
    Alert,
    AlertIcon,
    Spinner,
    Badge,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Radio,
    RadioGroup,
    Checkbox,
    Input,
    useToast
} from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import getSegments from 'graphql/queries/getSegments'
import getCampaigns from 'graphql/queries/getCampaigns'
import { Segment, Campaign } from 'graphql/types'

interface Props {
    initialLogic?: string
    onSave: (logic: string) => void
    onCancel: () => void
}

type LogicOperator = 'AND' | 'OR' | 'NOR' | 'NAND' | 'XOR'
type NodeType = 'segment' | 'campaign'

interface LogicNode {
    id: string
    type: NodeType
    segmentId: string | null
    campaignId: string | null
    operator: LogicOperator | null
    isNegated: boolean
    hasDuration: boolean
    durationDays: number | null
}

export const LogicBuilder = ({ initialLogic, onSave, onCancel }: Props) => {
    const toast = useToast()

    const { loading: segmentsLoading, error: segmentsError, data: segmentsData } = useQuery(getSegments, {
        variables: {
            AdminUserID: 'true'
        }
    })

    const { loading: campaignsLoading, error: campaignsError, data: campaignsData } = useQuery(getCampaigns)

    // Simple UUID generator fallback
    const generateId = () => {
        return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    }

    const [nodes, setNodes] = React.useState<LogicNode[]>([
        { id: generateId(), type: 'segment', segmentId: null, campaignId: null, operator: null, isNegated: false, hasDuration: false, durationDays: null }
    ])

    const parseLogicString = React.useCallback((logicString: string): LogicNode[] => {
        if (!logicString || !segmentsData?.getSegments || !campaignsData?.getCampaigns) {
            return [{ id: generateId(), type: 'segment', segmentId: null, campaignId: null, operator: null, isNegated: false, hasDuration: false, durationDays: null }]
        }

        const segments = segmentsData.getSegments || []
        const campaigns = campaignsData.getCampaigns || []

        // Regex patterns
        const notRegex = /^NOT\s+/
        const durationRegex = /\[(\d+)days\]$/
        const campaignPrefix = 'Campaign:'

        // Split by operators while keeping them
        const tokens: string[] = []
        let currentToken = ''
        const words = logicString.split(/\s+/)

        for (let i = 0; i < words.length; i++) {
            const word = words[i]
            if (['AND', 'OR', 'NOR', 'NAND', 'XOR'].includes(word)) {
                if (currentToken) {
                    tokens.push(currentToken.trim())
                    currentToken = ''
                }
                tokens.push(word)
            } else {
                currentToken += (currentToken ? ' ' : '') + word
            }
        }
        if (currentToken) {
            tokens.push(currentToken.trim())
        }

        const parsedNodes: LogicNode[] = []
        let currentOperator: LogicOperator | null = null

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i]

            // Check if token is an operator
            if (['AND', 'OR', 'NOR', 'NAND', 'XOR'].includes(token)) {
                currentOperator = token as LogicOperator
                continue
            }

            // Parse the condition
            let condition = token
            let isNegated = false
            let hasDuration = false
            let durationDays: number | null = null

            // Check for NOT prefix
            if (notRegex.test(condition)) {
                isNegated = true
                condition = condition.replace(notRegex, '')
            }

            // Check for duration suffix
            const durationMatch = condition.match(durationRegex)
            if (durationMatch) {
                hasDuration = true
                durationDays = parseInt(durationMatch[1])
                condition = condition.replace(durationRegex, '')
            }

            // Determine if it's a campaign or segment
            let type: NodeType = 'segment'
            let segmentId: string | null = null
            let campaignId: string | null = null

            if (condition.startsWith(campaignPrefix)) {
                type = 'campaign'
                const campaignName = condition.substring(campaignPrefix.length)
                const campaign = campaigns.find((c: Campaign) => c.Name === campaignName)
                campaignId = campaign?.ID || null
            } else {
                const segment = segments.find((s: Segment) => s.Name === condition)
                segmentId = segment?.ID || null
            }

            parsedNodes.push({
                id: generateId(),
                type,
                segmentId,
                campaignId,
                operator: i === 0 ? null : currentOperator,
                isNegated,
                hasDuration,
                durationDays
            })

            currentOperator = null
        }

        return parsedNodes.length > 0 ? parsedNodes : [
            { id: generateId(), type: 'segment', segmentId: null, campaignId: null, operator: null, isNegated: false, hasDuration: false, durationDays: null }
        ]
    }, [segmentsData, campaignsData])

    React.useEffect(() => {
        if (initialLogic && segmentsData?.getSegments && campaignsData?.getCampaigns) {
            const parsedNodes = parseLogicString(initialLogic)
            setNodes(parsedNodes)
        }
    }, [initialLogic, segmentsData, campaignsData, parseLogicString])

    const addNode = () => {
        setNodes([
            ...nodes,
            { id: generateId(), type: 'segment', segmentId: null, campaignId: null, operator: 'AND', isNegated: false, hasDuration: false, durationDays: null }
        ])
    }

    const removeNode = (id: string) => {
        if (nodes.length > 1) {
            setNodes(nodes.filter((node) => node.id !== id))
        }
    }

    const updateNodeType = (id: string, type: NodeType) => {
        setNodes(
            nodes.map((node) =>
                node.id === id ? { ...node, type, segmentId: null, campaignId: null } : node
            )
        )
    }

    const updateSegment = (id: string, segmentId: string) => {
        setNodes(
            nodes.map((node) =>
                node.id === id ? { ...node, segmentId, campaignId: null } : node
            )
        )
    }

    const updateCampaign = (id: string, campaignId: string) => {
        setNodes(
            nodes.map((node) =>
                node.id === id ? { ...node, campaignId, segmentId: null } : node
            )
        )
    }

    const updateOperator = (id: string, operator: LogicOperator) => {
        setNodes(
            nodes.map((node) =>
                node.id === id ? { ...node, operator } : node
            )
        )
    }

    const toggleNegation = (id: string) => {
        setNodes(
            nodes.map((node) =>
                node.id === id ? { ...node, isNegated: !node.isNegated } : node
            )
        )
    }

    const toggleDuration = (id: string) => {
        setNodes(
            nodes.map((node) =>
                node.id === id ? { ...node, hasDuration: !node.hasDuration, durationDays: !node.hasDuration ? 30 : null } : node
            )
        )
    }

    const updateDurationDays = (id: string, days: number) => {
        setNodes(
            nodes.map((node) =>
                node.id === id ? { ...node, durationDays: days } : node
            )
        )
    }

    const generateLogicString = (): string => {
        const segments = segmentsData?.getSegments || []
        const campaigns = campaignsData?.getCampaigns || []

        return nodes
            .map((node, index) => {
                let name = 'NONE'

                if (node.type === 'segment' && node.segmentId) {
                    const segment = segments.find((s: Segment) => s.ID === node.segmentId)
                    name = segment?.Name || node.segmentId
                } else if (node.type === 'campaign' && node.campaignId) {
                    const campaign = campaigns.find((c: Campaign) => c.ID === node.campaignId)
                    name = `Campaign:${campaign?.Name || node.campaignId}`
                }

                // Add duration suffix if enabled
                if (node.hasDuration && node.durationDays) {
                    name = `${name}[${node.durationDays}days]`
                }

                const notPrefix = node.isNegated ? 'NOT ' : ''

                if (index === 0) {
                    return `${notPrefix}${name}`
                }

                return `${node.operator || 'AND'} ${notPrefix}${name}`
            })
            .join(' ')
    }

    const handleSave = () => {
        // Validate that all nodes have either a segment or campaign selected
        const invalidNodes = nodes.filter(node => !node.segmentId && !node.campaignId)

        if (invalidNodes.length > 0) {
            toast({
                title: 'Validation Error',
                description: 'Please select a segment or campaign for all conditions before saving.',
                status: 'error',
                duration: 5000,
                isClosable: true
            })
            return
        }

        const logicString = generateLogicString()
        onSave(logicString)
    }

    if (segmentsLoading || campaignsLoading) {
        return (
            <Box p={4} textAlign="center">
                <Spinner />
                <Text mt={2}>Loading segments and campaigns...</Text>
            </Box>
        )
    }

    if (segmentsError || campaignsError) {
        return (
            <Alert status="error">
                <AlertIcon />
                Error loading data: {segmentsError?.message || campaignsError?.message}
            </Alert>
        )
    }

    const segments = (segmentsData?.getSegments || [])
    const campaigns = (campaignsData?.getCampaigns || [])
    const logicPreview = generateLogicString()

    return (
        <VStack spacing={4} align="stretch" p={4} w="full">
            <Box>
                <Text fontSize="lg" fontWeight="bold" mb={2}>
                    Logic Builder
                </Text>
                <Text fontSize="sm" color="gray.600">
                    Build campaign logic by combining segments and campaigns with operators
                </Text>
            </Box>

            <Box
                p={3}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
            >
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                    Preview:
                </Text>
                <Text
                    fontFamily="monospace"
                    fontSize="sm"
                    color={logicPreview === 'NONE' ? 'gray.400' : 'gray.800'}
                >
                    {logicPreview || 'No logic configured'}
                </Text>
            </Box>

            <VStack spacing={3} align="stretch">
                {nodes.map((node, index) => (
                    <Box key={node.id}>
                        {index > 0 && (
                            <HStack mb={2} justify="center">
                                <Menu>
                                    <MenuButton
                                        as={Button}
                                        size="sm"
                                        colorScheme="purple"
                                        variant="solid"
                                    >
                                        {node.operator || 'AND'}
                                    </MenuButton>
                                    <MenuList>
                                        <MenuItem
                                            onClick={() => updateOperator(node.id, 'AND')}
                                        >
                                            AND - All conditions must be true
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => updateOperator(node.id, 'OR')}
                                        >
                                            OR - Any condition must be true
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => updateOperator(node.id, 'NOR')}
                                        >
                                            NOR - None of the conditions are true
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => updateOperator(node.id, 'NAND')}
                                        >
                                            NAND - Not all conditions are true
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => updateOperator(node.id, 'XOR')}
                                        >
                                            XOR - Exactly one condition is true
                                        </MenuItem>
                                    </MenuList>
                                </Menu>
                            </HStack>
                        )}
                        <VStack align="stretch" spacing={2}>
                            <RadioGroup
                                value={node.type}
                                onChange={(value: string) => updateNodeType(node.id, value as NodeType)}
                            >
                                <HStack spacing={4}>
                                    <Radio value="segment">Segment</Radio>
                                    <Radio value="campaign">Campaign</Radio>
                                </HStack>
                            </RadioGroup>
                            <HStack>
                                {node.type === 'segment' ? (
                                    <Select
                                        placeholder="Select segment"
                                        value={node.segmentId || ''}
                                        onChange={(e) => updateSegment(node.id, e.target.value)}
                                        flex={1}
                                    >
                                        {segments.map((segment: Segment) => (
                                            <option key={segment.ID} value={segment.ID}>
                                                {segment.Name} ({segment.ID})
                                            </option>
                                        ))}
                                    </Select>
                                ) : (
                                    <Select
                                        placeholder="Select campaign"
                                        value={node.campaignId || ''}
                                        onChange={(e) => updateCampaign(node.id, e.target.value)}
                                        flex={1}
                                    >
                                        {campaigns.map((campaign: Campaign) => (
                                            <option key={campaign.ID} value={campaign.ID}>
                                                {campaign.Name} ({campaign.ID})
                                            </option>
                                        ))}
                                    </Select>
                                )}
                                {(node.segmentId || node.campaignId) && (
                                    <Badge colorScheme="green">Selected</Badge>
                                )}
                                <Button
                                    size="sm"
                                    colorScheme={node.isNegated ? 'orange' : 'gray'}
                                    variant={node.isNegated ? 'solid' : 'outline'}
                                    onClick={() => toggleNegation(node.id)}
                                    minW="60px"
                                >
                                    NOT
                                </Button>
                                <Button
                                    size="sm"
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={() => removeNode(node.id)}
                                    isDisabled={nodes.length === 1}
                                >
                                    Remove
                                </Button>
                            </HStack>
                            <HStack spacing={2} pl={2}>
                                <Checkbox
                                    isChecked={node.hasDuration}
                                    onChange={() => toggleDuration(node.id)}
                                    size="sm"
                                >
                                    <Text fontSize="sm">Duration (days)</Text>
                                </Checkbox>
                                {node.hasDuration && (
                                    <Input
                                        type="number"
                                        value={node.durationDays || ''}
                                        onChange={(e) => updateDurationDays(node.id, parseInt(e.target.value) || 0)}
                                        placeholder="Days"
                                        size="sm"
                                        width="100px"
                                        min={1}
                                    />
                                )}
                            </HStack>
                        </VStack>
                    </Box>
                ))}
            </VStack>

            <Button
                onClick={addNode}
                variant="outline"
                colorScheme="blue"
                size="sm"
            >
                Add Condition
            </Button>

            <HStack spacing={3} pt={4}>
                <Button
                    colorScheme="blue"
                    onClick={handleSave}
                    flex={1}
                    isDisabled={nodes.some((node) => !node.segmentId && !node.campaignId)}
                >
                    Save Logic
                </Button>
                <Button onClick={onCancel} variant="outline" flex={1}>
                    Cancel
                </Button>
            </HStack>
        </VStack>
    )
}
